import { d, q, y, T, u, r as register } from "./preact-core.js";
import { l as layout, u as updateCartTrigger, a as updateCartDom } from "./layout-store.js";
import "./preact-signals.js";
const API_TIMEOUT_MS = 1e4;
function useGiftCardApi({
  pairedProductHandle,
  pairedProductId
}) {
  const [state, setState] = d({
    ready: false,
    error: null,
    currentProductType: null,
    pairedProductType: null,
    pairedProductData: null,
    digitalVariants: [],
    digitalAmounts: [],
    digitalImages: [],
    physicalVariants: [],
    physicalAmounts: [],
    physicalImages: [],
    // Config defaults
    digitalCustomAmountEnabled: false,
    digitalCustomAmountMin: 1e3,
    // $10 default
    digitalCustomAmountMax: null,
    digitalCustomAmountVariantId: null
  });
  const getProductType = q(
    (variants) => {
      if (!variants || variants.length === 0) {
        return null;
      }
      const requiresShipping = variants[0].requires_shipping;
      return requiresShipping ? "physical" : "digital";
    },
    []
  );
  const mapShopifyVariantsToAmounts = q(
    (variants) => {
      return variants.map((v) => ({
        id: v.id,
        name: v.title,
        price: Math.round(parseFloat(v.price) * 100)
      }));
    },
    []
  );
  const fetchPairedProductByHandle = q(
    async (handle) => {
      try {
        const response = await fetch(`/products/${handle}.json`);
        if (!response.ok) {
          throw new Error("Failed to fetch paired product");
        }
        const data = await response.json();
        return data.product;
      } catch (error) {
        console.error("Error fetching paired product by handle:", error);
        return null;
      }
    },
    []
  );
  const fetchPairedProductById = q(
    async (productId) => {
      var _a;
      try {
        const response = await fetch("/products.json?limit=250");
        if (!response.ok) {
          throw new Error("Failed to fetch products");
        }
        const data = await response.json();
        const product = (_a = data.products) == null ? void 0 : _a.find(
          (p) => String(p.id) === String(productId)
        );
        return product || null;
      } catch (error) {
        console.error("Error fetching paired product by ID:", error);
        return null;
      }
    },
    []
  );
  const initializeApiData = q(async () => {
    var _a, _b, _c, _d, _e;
    try {
      const { GiftHeroAPI } = window;
      if (!GiftHeroAPI) {
        throw new Error("GiftHeroAPI not available");
      }
      const variants = await GiftHeroAPI.getVariants();
      const options = await GiftHeroAPI.getOptions();
      const images = await GiftHeroAPI.getImages();
      const currentProductType = getProductType(variants);
      const amounts = ((_a = options == null ? void 0 : options[0]) == null ? void 0 : _a.values) || [];
      let digitalVariants = [];
      let digitalAmounts = [];
      let digitalImages = [];
      let physicalVariants = [];
      let physicalAmounts = [];
      let physicalImages = [];
      if (currentProductType === "digital") {
        digitalVariants = variants || [];
        digitalAmounts = amounts;
        digitalImages = images || [];
      } else if (currentProductType === "physical") {
        physicalVariants = variants || [];
        physicalAmounts = amounts;
        physicalImages = images || [];
      }
      let pairedProductType = null;
      let pairedProductData = null;
      if (pairedProductId) {
        pairedProductData = await fetchPairedProductById(pairedProductId);
      } else if (pairedProductHandle) {
        pairedProductData = await fetchPairedProductByHandle(pairedProductHandle);
      }
      if (pairedProductData) {
        pairedProductType = getProductType(pairedProductData.variants);
        const pairedAmounts = mapShopifyVariantsToAmounts(
          pairedProductData.variants
        );
        const pairedImages = ((_b = pairedProductData.featured_image) == null ? void 0 : _b.src) ? [pairedProductData.featured_image.src] : [];
        if (pairedProductType === "digital") {
          digitalVariants = pairedProductData.variants || [];
          digitalAmounts = pairedAmounts;
          digitalImages = pairedImages;
        } else if (pairedProductType === "physical") {
          physicalVariants = pairedProductData.variants || [];
          physicalAmounts = pairedAmounts;
          physicalImages = pairedImages;
        }
      }
      const defaultImage = images && images.length > 0 ? images[0] : null;
      if (defaultImage && GiftHeroAPI.setImage) {
        await GiftHeroAPI.setImage(defaultImage);
      }
      const config = GiftHeroAPI.config || {};
      const digitalCustomAmountEnabled = config.customAmount === true;
      const digitalCustomAmountMin = ((_c = config.customLimit) == null ? void 0 : _c.min) ? parseInt(config.customLimit.min) * 100 : 1e3;
      const digitalCustomAmountMax = ((_d = config.customLimit) == null ? void 0 : _d.max) ? parseInt(config.customLimit.max) * 100 : null;
      const customAmountVariantGid = (_e = config.systemProduct) == null ? void 0 : _e.customAmountVariantId;
      const digitalCustomAmountVariantId = customAmountVariantGid ? customAmountVariantGid.split("/").pop() || null : null;
      setState({
        ready: true,
        error: null,
        currentProductType,
        pairedProductType,
        pairedProductData,
        digitalVariants,
        digitalAmounts,
        digitalImages,
        physicalVariants,
        physicalAmounts,
        physicalImages,
        digitalCustomAmountEnabled,
        digitalCustomAmountMin,
        digitalCustomAmountMax,
        digitalCustomAmountVariantId
      });
    } catch (error) {
      console.error("Failed to initialize Gift Card API:", error);
      setState((prev) => ({
        ...prev,
        error: "Failed to load gift card options. Please refresh the page."
      }));
    }
  }, [pairedProductHandle, pairedProductId, getProductType, mapShopifyVariantsToAmounts, fetchPairedProductByHandle, fetchPairedProductById]);
  y(() => {
    let timeoutId;
    const handleApiInitialized = () => {
      clearTimeout(timeoutId);
      initializeApiData();
    };
    if (window.GiftHeroAPI) {
      initializeApiData();
    } else {
      window.addEventListener("gift.hero:initalized", handleApiInitialized);
      timeoutId = setTimeout(() => {
        if (!state.ready) {
          setState((prev) => ({
            ...prev,
            error: "Gift card system is currently unavailable. Please try again later."
          }));
        }
      }, API_TIMEOUT_MS);
    }
    return () => {
      window.removeEventListener("gift.hero:initalized", handleApiInitialized);
      clearTimeout(timeoutId);
    };
  }, [initializeApiData, state.ready]);
  return state;
}
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(email) {
  return EMAIL_REGEX.test(email.trim());
}
function validateAmount(isCustomAmount, selectedAmount, customAmountInput, customAmountCents, minCustomAmount) {
  const errors = {};
  if (isCustomAmount) {
    if (!customAmountInput.trim()) {
      errors.customAmount = "Please enter a custom amount";
    } else if (!customAmountCents || customAmountCents <= 0) {
      errors.customAmount = "Please enter a valid amount";
    } else if (minCustomAmount && customAmountCents < minCustomAmount) {
      errors.customAmount = `Minimum amount is $${(minCustomAmount / 100).toFixed(2)}`;
    } else if (customAmountCents > 1e6) {
      errors.customAmount = "Maximum amount is $10,000.00";
    }
  } else {
    if (!selectedAmount) {
      errors.amount = "Please select an amount";
    }
  }
  return errors;
}
function validateRecipientFields(sendAsGift, recipientEmail, recipientName, senderName, message) {
  if (!sendAsGift) {
    return {};
  }
  const errors = {};
  if (!recipientEmail.trim()) {
    errors.recipientEmail = "Recipient email is required";
  } else if (!isValidEmail(recipientEmail)) {
    errors.recipientEmail = "Please enter a valid email address";
  }
  if (!recipientName.trim()) {
    errors.recipientName = "Recipient name is required";
  }
  if (!senderName.trim()) {
    errors.senderName = "Your name is required";
  }
  if (!message.trim()) {
    errors.message = "A message is required when sending as a gift";
  }
  return errors;
}
function validateDeliveryDate(deliveryDate) {
  if (!deliveryDate) {
    return void 0;
  }
  const selectedDate = new Date(deliveryDate);
  const today = /* @__PURE__ */ new Date();
  today.setHours(0, 0, 0, 0);
  if (selectedDate < today) {
    return "Delivery date cannot be in the past";
  }
  return void 0;
}
function validateForm(state, minCustomAmount) {
  const amountErrors = validateAmount(
    state.isCustomAmount,
    state.selectedAmount,
    state.customAmountInput,
    state.customAmountCents,
    minCustomAmount
  );
  const recipientErrors = validateRecipientFields(
    state.sendAsGift,
    state.recipientEmail,
    state.recipientName,
    state.senderName,
    state.message
  );
  const deliveryDateError = validateDeliveryDate(state.deliveryDate);
  return {
    ...amountErrors,
    ...recipientErrors,
    ...deliveryDateError ? { deliveryDate: deliveryDateError } : {}
  };
}
function parseCustomAmount(value) {
  const sanitized = value.replace(/[^\d.]/g, "");
  const parts = sanitized.split(".");
  const formatted = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : sanitized;
  let cents = null;
  if (formatted) {
    const floatValue = parseFloat(formatted);
    if (!isNaN(floatValue)) {
      cents = Math.round(floatValue * 100);
    }
  }
  return { formatted, cents };
}
function debugLog(context, data) {
  return;
}
function getMinimumCustomAmount(amounts) {
  if (amounts.length === 0) return 1e3;
  const prices = amounts.filter((a) => a.price > 0).map((a) => a.price);
  return prices.length > 0 ? Math.min(...prices) : 1e3;
}
function useGiftCardForm({
  api,
  productId
}) {
  const [state, setState] = d({
    cardType: null,
    selectedAmount: null,
    selectedAmountId: null,
    isCustomAmount: false,
    customAmountInput: "",
    customAmountCents: null,
    sendAsGift: false,
    recipientEmail: "",
    recipientName: "",
    message: "",
    senderName: "",
    deliveryDate: "",
    qty: 1,
    submitting: false,
    errors: {}
  });
  y(() => {
    if (api.ready && !state.cardType) {
      const defaultCardType = api.currentProductType;
      const amounts = defaultCardType === "digital" ? api.digitalAmounts : api.physicalAmounts;
      setState((prev) => ({
        ...prev,
        cardType: defaultCardType,
        selectedAmount: amounts.length > 0 ? amounts[0].price : null,
        selectedAmountId: amounts.length > 0 ? amounts[0].id : null
      }));
    }
  }, [api.ready, api.currentProductType, api.digitalAmounts, api.physicalAmounts, state.cardType]);
  const availableAmounts = T(() => {
    return state.cardType === "digital" ? api.digitalAmounts : api.physicalAmounts;
  }, [state.cardType, api.digitalAmounts, api.physicalAmounts]);
  const availableVariants = T(() => {
    return state.cardType === "digital" ? api.digitalVariants : api.physicalVariants;
  }, [state.cardType, api.digitalVariants, api.physicalVariants]);
  const availableImages = T(() => {
    return state.cardType === "digital" ? api.digitalImages : api.physicalImages;
  }, [state.cardType, api.digitalImages, api.physicalImages]);
  const minCustomAmount = T(() => {
    if (state.cardType === "digital") {
      return api.digitalCustomAmountMin;
    }
    return getMinimumCustomAmount(availableAmounts);
  }, [state.cardType, api.digitalCustomAmountMin, availableAmounts]);
  const hasCustomAmountVariant = T(() => {
    if (state.cardType === "digital") {
      return api.digitalCustomAmountEnabled;
    }
    return availableVariants.some(
      (v) => {
        var _a;
        return (_a = v.title) == null ? void 0 : _a.toLowerCase().includes("custom");
      }
    );
  }, [state.cardType, api.digitalCustomAmountEnabled, availableVariants]);
  const hasPairedProduct = T(() => {
    return api.pairedProductData !== null;
  }, [api.pairedProductData]);
  const handleCardTypeChange = q(
    (type) => {
      const amounts = type === "digital" ? api.digitalAmounts : api.physicalAmounts;
      setState((prev) => {
        var _a, _b;
        return {
          ...prev,
          cardType: type,
          selectedAmount: ((_a = amounts[0]) == null ? void 0 : _a.price) || null,
          selectedAmountId: ((_b = amounts[0]) == null ? void 0 : _b.id) || null,
          isCustomAmount: false,
          customAmountInput: "",
          customAmountCents: null,
          errors: {}
        };
      });
    },
    [api.digitalAmounts, api.physicalAmounts]
  );
  const handleAmountSelect = q((amount) => {
    setState((prev) => ({
      ...prev,
      selectedAmount: amount.price,
      selectedAmountId: amount.id,
      isCustomAmount: false,
      customAmountInput: "",
      customAmountCents: null,
      errors: {
        ...prev.errors,
        amount: void 0,
        customAmount: void 0
      }
    }));
  }, []);
  const handleCustomAmountToggle = q(() => {
    setState((prev) => ({
      ...prev,
      isCustomAmount: true,
      selectedAmount: null,
      selectedAmountId: null,
      customAmountInput: "",
      customAmountCents: null,
      errors: {
        ...prev.errors,
        amount: void 0,
        customAmount: void 0
      }
    }));
  }, []);
  const handleCustomAmountInput = q((value) => {
    const { formatted, cents } = parseCustomAmount(value);
    setState((prev) => ({
      ...prev,
      customAmountInput: formatted,
      customAmountCents: cents,
      errors: { ...prev.errors, customAmount: void 0 }
    }));
  }, []);
  const handleSendAsGiftToggle = q((value) => {
    setState((prev) => ({
      ...prev,
      sendAsGift: value,
      deliveryDate: value ? prev.deliveryDate : "",
      errors: {
        ...prev.errors,
        recipientEmail: void 0,
        recipientName: void 0,
        senderName: void 0,
        message: void 0,
        deliveryDate: void 0
      }
    }));
  }, []);
  const handleRecipientFieldChange = q(
    (field, value) => {
      setState((prev) => ({
        ...prev,
        [field]: value,
        errors: { ...prev.errors, [field]: void 0 }
      }));
    },
    []
  );
  const handleDeliveryDateChange = q((value) => {
    setState((prev) => ({
      ...prev,
      deliveryDate: value,
      errors: { ...prev.errors, deliveryDate: void 0 }
    }));
  }, []);
  const handleSubmit = q(
    async (evt) => {
      var _a;
      evt.preventDefault();
      debugLog("Form submission started", {
        cardType: state.cardType,
        isCustomAmount: state.isCustomAmount,
        selectedAmount: state.selectedAmount,
        selectedAmountId: state.selectedAmountId,
        customAmountCents: state.customAmountCents,
        sendAsGift: state.sendAsGift,
        currentProductType: api.currentProductType
      });
      const errors = validateForm(state, minCustomAmount);
      if (Object.keys(errors).length > 0) {
        setState((prev) => ({ ...prev, errors }));
        return;
      }
      setState((prev) => ({ ...prev, submitting: true }));
      layout.cartError.value = null;
      try {
        let variantId;
        let variantPrice;
        if (state.isCustomAmount) {
          variantPrice = state.customAmountCents;
          debugLog("Custom amount detected", { variantPrice });
        } else {
          let selectedVariant = availableVariants.find(
            (v) => v.id === state.selectedAmountId
          );
          if (!selectedVariant && availableVariants.length > 0) {
            selectedVariant = availableVariants[0];
          }
          variantId = (selectedVariant == null ? void 0 : selectedVariant.id) || state.selectedAmountId;
          variantPrice = state.selectedAmount;
          debugLog("Preset amount selected", { variantId, variantPrice });
        }
        const useGiftHeroApi = state.cardType === "digital" && api.currentProductType === "digital";
        debugLog("API routing decision", {
          useGiftHeroApi,
          cardType: state.cardType,
          currentProductType: api.currentProductType,
          isCustomAmount: state.isCustomAmount
        });
        if (useGiftHeroApi) {
          const { GiftHeroAPI } = window;
          if (!GiftHeroAPI) {
            throw new Error("Gift Card Hero API is not available");
          }
          if (state.isCustomAmount) {
            const amountCents = state.customAmountCents;
            const amountDollars = (amountCents / 100).toFixed(2);
            debugLog("Calling setCustomAmount", {
              amountCents,
              amountDollars
            });
            if (typeof GiftHeroAPI.setCustomAmount === "function") {
              try {
                await GiftHeroAPI.setCustomAmount(amountCents);
                debugLog("setCustomAmount succeeded", {
                  format: "cents (number)",
                  value: amountCents
                });
              } catch (err) {
                debugLog("setCustomAmount failed with cents", err);
                throw err;
              }
            } else {
              debugLog("setCustomAmount method not available on GiftHeroAPI");
              throw new Error("setCustomAmount is not available");
            }
          } else {
            if (typeof GiftHeroAPI.setVariant === "function" && variantId) {
              debugLog("Calling setVariant for preset amount", { variantId });
              try {
                await GiftHeroAPI.setVariant(variantId);
                debugLog("setVariant succeeded", { variantId });
              } catch (err) {
                debugLog("setVariant failed (may be optional)", err);
              }
            }
          }
          const cardData = {};
          if (!state.isCustomAmount && variantId) {
            cardData.variantId = variantId;
          }
          if (availableImages[0]) {
            cardData.image = availableImages[0];
          }
          if (state.sendAsGift) {
            cardData.message = state.message;
            cardData.from = state.senderName;
            cardData.email = state.recipientEmail;
            if (state.recipientName) {
              cardData.recipientName = state.recipientName;
            }
            if (state.deliveryDate) {
              cardData.deliveryDate = new Date(state.deliveryDate);
            }
          }
          debugLog("Calling setCardData", cardData);
          await GiftHeroAPI.setCardData(cardData);
          debugLog("setCardData succeeded");
          if (state.isCustomAmount) {
            const customVariantId = api.digitalCustomAmountVariantId;
            if (!customVariantId) {
              debugLog("Error: customAmountVariantId not found in config");
              throw new Error(
                "Custom amount variant not configured. Please contact support."
              );
            }
            const customCardData = {
              img: availableImages[0] || "",
              message: state.message || "",
              name: state.senderName || "",
              to: state.recipientName || "",
              email: state.recipientEmail || "",
              productId: parseInt(productId),
              variantId: customVariantId,
              variantPrice: state.customAmountCents,
              mv: `$${(state.customAmountCents / 100).toFixed(2)}`
            };
            debugLog("Custom amount - using Shopify Cart API", {
              customVariantId,
              qty: state.qty,
              cardData: customCardData
            });
            const response = await fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                id: customVariantId,
                quantity: state.qty,
                properties: {
                  __cardData: JSON.stringify(customCardData)
                }
              })
            });
            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              debugLog("Shopify Cart API failed", errorData);
              throw new Error("Failed to add custom amount to cart");
            }
            debugLog("Shopify Cart API succeeded for custom amount");
            await layout.fetchCart();
            debugLog("Redirecting to checkout for custom amount");
            window.location.href = "/checkout";
            return;
          }
          debugLog("Calling validate");
          const isValid = await GiftHeroAPI.validate();
          debugLog("validate result", { isValid });
          if (!isValid) {
            setState((prev) => ({
              ...prev,
              errors: { general: "Please check all required fields." },
              submitting: false
            }));
            return;
          }
          debugLog("Calling addToCart", { qty: state.qty });
          await GiftHeroAPI.addToCart(state.qty);
          debugLog("addToCart succeeded");
        } else {
          debugLog("Using Shopify Cart API");
          let pairedVariantId = variantId;
          if (state.isCustomAmount) {
            const variantsToSearch = state.cardType === "digital" ? api.digitalVariants : api.physicalVariants;
            const customVariant = variantsToSearch.find(
              (v) => {
                var _a2;
                return (_a2 = v.title) == null ? void 0 : _a2.toLowerCase().includes("custom");
              }
            );
            if (customVariant) {
              pairedVariantId = customVariant.id;
            } else {
              throw new Error(
                `Custom amount variant not found for ${state.cardType} card`
              );
            }
          }
          debugLog("Shopify Cart API request", {
            pairedVariantId,
            qty: state.qty,
            isCustomAmount: state.isCustomAmount
          });
          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: pairedVariantId,
              quantity: state.qty,
              properties: state.isCustomAmount ? {
                Message: state.message || "",
                "Your name": state.senderName || "",
                "Recipient name": state.recipientName || "",
                __cardData: JSON.stringify({
                  img: availableImages[0] || "",
                  message: state.message || "",
                  name: state.senderName || "",
                  to: state.recipientName || "",
                  productId: parseInt(productId),
                  title: ((_a = api.pairedProductData) == null ? void 0 : _a.title) || "Craggy Range Physical Gift Card",
                  variantId,
                  variantTitle: "",
                  variantPrice,
                  mv: `$${(variantPrice / 100).toFixed(2)}`
                })
              } : {}
            })
          });
          if (!response.ok) {
            throw new Error("Failed to add to cart");
          }
          debugLog("Shopify Cart API succeeded");
        }
        debugLog("Updating cart UI");
        await layout.fetchCart();
        updateCartTrigger();
        updateCartDom();
        if (state.isCustomAmount) {
          debugLog("Redirecting to checkout for custom amount");
          window.location.href = "/checkout";
        } else {
          debugLog("Opening cart slideover");
          layout.slideover.value = "cart";
        }
        setState((prev) => ({
          ...prev,
          sendAsGift: false,
          deliveryDate: "",
          recipientEmail: "",
          recipientName: "",
          message: "",
          senderName: "",
          isCustomAmount: false,
          customAmountInput: "",
          customAmountCents: null,
          errors: {},
          submitting: false
        }));
      } catch (error) {
        console.error("Failed to add gift card to cart:", error);
        debugLog("Submission error", {
          message: error.message,
          stack: error.stack
        });
        setState((prev) => ({
          ...prev,
          errors: {
            general: error.message || "Failed to add gift card to cart. Please try again."
          },
          submitting: false
        }));
      }
    },
    [
      state,
      minCustomAmount,
      availableVariants,
      availableImages,
      api.currentProductType,
      api.digitalVariants,
      api.physicalVariants,
      api.pairedProductData,
      api.digitalCustomAmountVariantId,
      productId
    ]
  );
  return {
    state,
    availableAmounts,
    availableVariants,
    availableImages,
    minCustomAmount,
    hasCustomAmountVariant,
    hasPairedProduct,
    handleCardTypeChange,
    handleAmountSelect,
    handleCustomAmountToggle,
    handleCustomAmountInput,
    handleSendAsGiftToggle,
    handleRecipientFieldChange,
    handleDeliveryDateChange,
    handleSubmit
  };
}
function LoadingAndError({ isLoading, error }) {
  if (isLoading) {
    return /* @__PURE__ */ u("div", { class: "gift-card-form", children: /* @__PURE__ */ u("div", { class: "gift-card-form__loading", children: /* @__PURE__ */ u("p", { children: "Loading gift card options..." }) }) });
  }
  if (error) {
    return /* @__PURE__ */ u("div", { class: "gift-card-form", children: /* @__PURE__ */ u("div", { class: "gift-card-form__error", children: /* @__PURE__ */ u("p", { children: error }) }) });
  }
  return null;
}
function CardTypeSelector({
  cardType,
  hasDigital,
  hasPhysical,
  onCardTypeChange
}) {
  return /* @__PURE__ */ u("div", { class: "gift-card-form__card-type mb-20", children: [
    /* @__PURE__ */ u("h3", { class: "h5 mb-10", children: "Card Type" }),
    /* @__PURE__ */ u("div", { class: "grid grid-cols-2 gap-10", children: [
      hasDigital && /* @__PURE__ */ u("div", { class: "form-group--radio", children: [
        /* @__PURE__ */ u(
          "input",
          {
            type: "radio",
            id: "card-type-digital",
            name: "cardType",
            checked: cardType === "digital",
            onChange: () => onCardTypeChange("digital")
          }
        ),
        /* @__PURE__ */ u("label", { for: "card-type-digital", children: "E-Gift Card" })
      ] }),
      hasPhysical && /* @__PURE__ */ u("div", { class: "form-group--radio", children: [
        /* @__PURE__ */ u(
          "input",
          {
            type: "radio",
            id: "card-type-physical",
            name: "cardType",
            checked: cardType === "physical",
            onChange: () => onCardTypeChange("physical")
          }
        ),
        /* @__PURE__ */ u("label", { for: "card-type-physical", children: "Physical Gift Card" })
      ] })
    ] })
  ] });
}
function AmountSelector({
  amounts,
  selectedAmount,
  isCustomAmount,
  customAmountInput,
  minCustomAmount,
  hasCustomAmountVariant,
  errors,
  onAmountSelect,
  onCustomAmountToggle,
  onCustomAmountInput
}) {
  const presetAmounts = amounts.filter((amount) => {
    var _a, _b;
    const isCustomVariant = ((_a = amount.name) == null ? void 0 : _a.toLowerCase().includes("custom")) || ((_b = amount.title) == null ? void 0 : _b.toLowerCase().includes("custom"));
    return !isCustomVariant;
  });
  return /* @__PURE__ */ u("div", { class: "mb-20", children: [
    /* @__PURE__ */ u("h3", { class: "h5 mb-10", children: "Select Amount" }),
    /* @__PURE__ */ u("div", { class: "gift-card-form__amounts", children: [
      presetAmounts.map((amount) => /* @__PURE__ */ u(
        "button",
        {
          type: "button",
          class: `amount-btn ${selectedAmount === amount.price && !isCustomAmount ? "active" : ""}`,
          onClick: () => onAmountSelect(amount),
          children: [
            "$",
            (amount.price / 100).toFixed(0)
          ]
        },
        amount.id
      )),
      hasCustomAmountVariant && /* @__PURE__ */ u(
        "input",
        {
          type: "text",
          inputMode: "decimal",
          placeholder: "Custom",
          value: customAmountInput,
          onFocus: onCustomAmountToggle,
          onInput: (e) => onCustomAmountInput(e.target.value),
          class: `amount-btn amount-btn--custom ${isCustomAmount ? "active" : ""}`
        }
      )
    ] }),
    isCustomAmount && errors.customAmount && /* @__PURE__ */ u("span", { class: "error mt-10", children: errors.customAmount }),
    errors.amount && !isCustomAmount && /* @__PURE__ */ u("span", { class: "error", children: errors.amount })
  ] });
}
function RecipientFields({
  recipientEmail,
  recipientName,
  message,
  senderName,
  deliveryDate,
  errors,
  onFieldChange,
  onDeliveryDateChange
}) {
  return /* @__PURE__ */ u("div", { class: "gift-card-form__recipient-fields", children: [
    /* @__PURE__ */ u("h3", { class: "mb-15", children: "Gift Recipient Details" }),
    /* @__PURE__ */ u("div", { class: "form-group--styled mb-15", children: [
      /* @__PURE__ */ u(
        "input",
        {
          type: "email",
          id: "recipient-email",
          value: recipientEmail,
          onInput: (e) => onFieldChange("recipientEmail", e.target.value),
          class: recipientEmail ? "has-value" : ""
        }
      ),
      /* @__PURE__ */ u("label", { for: "recipient-email", children: "Recipient Email *" }),
      errors.recipientEmail && /* @__PURE__ */ u("span", { class: "error", children: errors.recipientEmail })
    ] }),
    /* @__PURE__ */ u("div", { class: "form-group--styled mb-15", children: [
      /* @__PURE__ */ u(
        "input",
        {
          type: "text",
          id: "recipient-name",
          value: recipientName,
          onInput: (e) => onFieldChange("recipientName", e.target.value),
          class: recipientName ? "has-value" : ""
        }
      ),
      /* @__PURE__ */ u("label", { for: "recipient-name", children: "Recipient Name *" }),
      errors.recipientName && /* @__PURE__ */ u("span", { class: "error", children: errors.recipientName })
    ] }),
    /* @__PURE__ */ u("div", { class: "form-group--styled form-group--textarea mb-15", children: [
      /* @__PURE__ */ u(
        "textarea",
        {
          id: "gift-message",
          value: message,
          onInput: (e) => onFieldChange("message", e.target.value),
          class: message ? "has-value" : "",
          rows: 3
        }
      ),
      /* @__PURE__ */ u("label", { for: "gift-message", children: "Your Message *" }),
      errors.message && /* @__PURE__ */ u("span", { class: "error", children: errors.message })
    ] }),
    /* @__PURE__ */ u("div", { class: "form-group--styled mb-15", children: [
      /* @__PURE__ */ u(
        "input",
        {
          type: "text",
          id: "sender-name",
          value: senderName,
          onInput: (e) => onFieldChange("senderName", e.target.value),
          class: senderName ? "has-value" : ""
        }
      ),
      /* @__PURE__ */ u("label", { for: "sender-name", children: "Your Name *" }),
      errors.senderName && /* @__PURE__ */ u("span", { class: "error", children: errors.senderName })
    ] }),
    /* @__PURE__ */ u("div", { class: "form-group--styled always-active mb-15", children: [
      /* @__PURE__ */ u(
        "input",
        {
          type: "date",
          id: "delivery-date",
          value: deliveryDate,
          onInput: (e) => onDeliveryDateChange(e.target.value),
          class: deliveryDate ? "has-value" : ""
        }
      ),
      /* @__PURE__ */ u("label", { for: "delivery-date", children: "Delivery Date (Optional)" }),
      errors.deliveryDate && /* @__PURE__ */ u("span", { class: "error", children: errors.deliveryDate })
    ] })
  ] });
}
function GiftCardForm({
  productId,
  pairedProductHandle,
  pairedProductId
}) {
  const api = useGiftCardApi({ pairedProductHandle, pairedProductId });
  const form = useGiftCardForm({
    api,
    productId
  });
  if (!api.ready || api.error) {
    return /* @__PURE__ */ u(LoadingAndError, { isLoading: !api.ready && !api.error, error: api.error });
  }
  const hasDigital = api.currentProductType === "digital" || api.pairedProductType === "digital";
  const hasPhysical = api.currentProductType === "physical" || api.pairedProductType === "physical";
  return /* @__PURE__ */ u("div", { class: "gift-card-form", children: [
    form.state.errors.general && /* @__PURE__ */ u("div", { class: "gift-card-form__error", children: /* @__PURE__ */ u("p", { children: form.state.errors.general }) }),
    form.hasPairedProduct && /* @__PURE__ */ u(
      CardTypeSelector,
      {
        cardType: form.state.cardType,
        hasDigital,
        hasPhysical,
        onCardTypeChange: form.handleCardTypeChange
      }
    ),
    /* @__PURE__ */ u(
      AmountSelector,
      {
        amounts: form.availableAmounts,
        selectedAmount: form.state.selectedAmount,
        isCustomAmount: form.state.isCustomAmount,
        customAmountInput: form.state.customAmountInput,
        minCustomAmount: form.minCustomAmount,
        hasCustomAmountVariant: form.hasCustomAmountVariant,
        errors: form.state.errors,
        onAmountSelect: form.handleAmountSelect,
        onCustomAmountToggle: form.handleCustomAmountToggle,
        onCustomAmountInput: form.handleCustomAmountInput
      }
    ),
    form.state.cardType === "digital" && /* @__PURE__ */ u("div", { class: "gift-card-form__send-toggle mb-20", children: [
      /* @__PURE__ */ u("h3", { class: "h5 mb-10", children: "Send To" }),
      /* @__PURE__ */ u("div", { class: "grid grid-cols-1 gap-5", children: [
        /* @__PURE__ */ u("div", { class: "form-group--radio", children: [
          /* @__PURE__ */ u(
            "input",
            {
              type: "radio",
              id: "send-to-self",
              name: "sendAsGift",
              checked: !form.state.sendAsGift,
              onChange: () => form.handleSendAsGiftToggle(false)
            }
          ),
          /* @__PURE__ */ u("label", { for: "send-to-self", children: "Send to myself" })
        ] }),
        /* @__PURE__ */ u("div", { class: "form-group--radio", children: [
          /* @__PURE__ */ u(
            "input",
            {
              type: "radio",
              id: "send-as-gift",
              name: "sendAsGift",
              checked: form.state.sendAsGift,
              onChange: () => form.handleSendAsGiftToggle(true)
            }
          ),
          /* @__PURE__ */ u("label", { for: "send-as-gift", children: "Send as a gift" })
        ] })
      ] })
    ] }),
    form.state.sendAsGift && /* @__PURE__ */ u(
      RecipientFields,
      {
        recipientEmail: form.state.recipientEmail,
        recipientName: form.state.recipientName,
        message: form.state.message,
        senderName: form.state.senderName,
        deliveryDate: form.state.deliveryDate,
        errors: form.state.errors,
        onFieldChange: form.handleRecipientFieldChange,
        onDeliveryDateChange: form.handleDeliveryDateChange
      }
    ),
    /* @__PURE__ */ u("div", { class: "gift-card-form__submit", children: /* @__PURE__ */ u(
      "button",
      {
        type: "button",
        class: `btn btn--primary w-full ${form.state.submitting ? "loading" : ""}`,
        onClick: form.handleSubmit,
        disabled: form.state.submitting,
        children: form.state.submitting ? "Adding to Cart..." : "Add to Cart"
      }
    ) })
  ] });
}
function GiftCardFormRegister() {
  register(GiftCardForm, "gift-card-form", [
    "product-id",
    "product-handle",
    "enable-physical-cards",
    "paired-product-handle",
    "paired-product-id"
  ]);
}
GiftCardFormRegister();
